import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { MessageCircle, Mail, Printer, Copy, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LOGO_URL } from "@/lib/brand";
import { api, formatApiError, formatDate } from "@/lib/api";

export const ShipmentLabel = ({ shipment }) => {
  const [sending, setSending] = useState(false);
  const trackingUrl = `${window.location.origin}/suivi/${shipment.tracking_number}`;
  const typeLabel = shipment.shipping_type === "aerien" ? "Aérien" : "Maritime";

  const message =
    `Bonjour ${shipment.recipient.name}, votre colis Haba Trans Logistique est en route.\n` +
    `Numéro de suivi : ${shipment.tracking_number}\n` +
    `Type : ${typeLabel} · Poids : ${shipment.weight_kg} kg\n` +
    `Livraison estimée : ${formatDate(shipment.estimated_delivery)}\n` +
    `Suivez votre colis ici : ${trackingUrl}`;

  const phone = (shipment.recipient.phone || "").replace(/[^\d]/g, "");

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="shipment-label-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900">QR code de suivi</h3>
          <p className="mt-1 text-sm text-slate-600">
            Le client scanne ce code pour voir le statut de son colis.
          </p>
          <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-slate-500"
             data-testid="label-recipient-email">
            {shipment.recipient_email
              ? `Email auto : ${shipment.recipient_email}`
              : "Aucun email destinataire — envoi automatique désactivé"}
          </p>
        </div>
      </div>

      <div id="printable-label" className="mt-5 rounded-xl border-2 border-dashed border-slate-300 p-5">
        <div className="flex items-center justify-between gap-4">
          <img src={LOGO_URL} alt="Haba Trans Logistique" className="h-8 w-auto object-contain" />
          <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
            {typeLabel} · {shipment.weight_kg} kg
          </span>
        </div>
        <div className="mt-4 flex flex-col items-center gap-4 sm:flex-row sm:items-center">
          <div className="rounded-lg bg-white p-2 ring-1 ring-slate-200">
            <QRCodeSVG value={trackingUrl} size={132} level="M" fgColor="#1E3A8A" data-testid="tracking-qrcode" />
          </div>
          <div className="min-w-0 text-center sm:text-left">
            <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Numéro de suivi</p>
            <p className="font-mono text-lg font-extrabold tracking-wider text-[#1E3A8A]"
               data-testid="label-tracking-number">
              {shipment.tracking_number}
            </p>
            <p className="mt-2 text-sm font-semibold text-slate-800">{shipment.recipient.name}</p>
            <p className="text-xs text-slate-600">
              {shipment.destination_city} · {shipment.recipient.phone}
            </p>
            <p className="mt-2 text-xs text-slate-500">
              Livraison estimée : {formatDate(shipment.estimated_delivery)}
            </p>
          </div>
        </div>
        <p className="mt-4 break-all text-center font-mono text-[10px] text-slate-400">{trackingUrl}</p>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <Button
          className="gap-2 bg-[#2563EB] font-semibold text-white hover:bg-[#1D4ED8]"
          disabled={sending || !shipment.recipient_email}
          onClick={async () => {
            setSending(true);
            try {
              const { data } = await api.post(`/shipments/${shipment.id}/notify`);
              toast.success(`Email de suivi envoyé à ${data.sent_to}`);
            } catch (e) {
              toast.error(formatApiError(e.response?.data?.detail));
            } finally {
              setSending(false);
            }
          }}
          data-testid="send-email-button"
        >
          <Send className="h-4 w-4" />
          {sending ? "Envoi…" : "Envoyer par email"}
        </Button>
        <Button
          className="gap-2 bg-[#16a34a] font-semibold text-white hover:bg-[#15803d]"
          onClick={() =>
            window.open(
              phone
                ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
                : `https://wa.me/?text=${encodeURIComponent(message)}`,
              "_blank",
            )
          }
          data-testid="share-whatsapp-button"
        >
          <MessageCircle className="h-4 w-4" /> WhatsApp
        </Button>
        <Button
          variant="outline"
          className="gap-2 border-slate-300"
          onClick={() =>
            window.open(
              `mailto:?subject=${encodeURIComponent(
                `Suivi de votre colis ${shipment.tracking_number} — Haba Trans Logistique`,
              )}&body=${encodeURIComponent(message)}`,
              "_self",
            )
          }
          data-testid="share-email-button"
        >
          <Mail className="h-4 w-4" /> Email
        </Button>
        <Button
          variant="outline"
          className="gap-2 border-slate-300"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(`${shipment.tracking_number} — ${trackingUrl}`);
              toast.success("Numéro et lien copiés");
            } catch {
              toast.error("Copie impossible. Sélectionnez le lien manuellement.");
            }
          }}
          data-testid="copy-tracking-link-button"
        >
          <Copy className="h-4 w-4" /> Copier
        </Button>
        <Button
          variant="outline"
          className="gap-2 border-slate-300"
          onClick={() => {
            document.body.classList.add("printing-label");
            window.print();
            document.body.classList.remove("printing-label");
          }}
          data-testid="print-label-button"
        >
          <Printer className="h-4 w-4" /> Imprimer l'étiquette
        </Button>
      </div>
    </section>
  );
};
